/**
 * Provider-neutral taxonomy for authentication failures.
 *
 * Auth handling used to branch on Auth0's English message strings. Entra ID and
 * Okta phrase the same failures differently, so every such match silently stops
 * working for a federated user. Each adapter maps its provider's errors onto
 * these codes, and the app branches on the code.
 */
export enum EAuthErrorCode {
  /** The provider authenticated the user but refused to authorise them. */
  ACCESS_DENIED = 'access_denied',
  /** No usable session at the provider; the user must log in interactively. */
  LOGIN_REQUIRED = 'login_required',
  /** The user must grant consent before a token can be issued. */
  CONSENT_REQUIRED = 'consent_required',
  /** A refresh or authorisation grant was rejected (rotation, revocation, clock skew). */
  INVALID_GRANT = 'invalid_grant',
  /** No refresh token is held, so a silent token renewal cannot even be attempted. */
  MISSING_REFRESH_TOKEN = 'missing_refresh_token',
  /** Nothing in the taxonomy matched — treat as transient and retryable. */
  UNKNOWN = 'unknown',
}

/**
 * Codes for which retrying is pointless: the session is broken and the user has
 * to re-authenticate.
 */
const NON_RECOVERABLE_AUTH_ERROR_CODES: readonly EAuthErrorCode[] = [
  EAuthErrorCode.MISSING_REFRESH_TOKEN,
  EAuthErrorCode.LOGIN_REQUIRED,
  EAuthErrorCode.CONSENT_REQUIRED,
  EAuthErrorCode.INVALID_GRANT,
  EAuthErrorCode.ACCESS_DENIED,
];

/**
 * Non-recoverable codes that are expected session-lifecycle transitions rather
 * than bugs. They are handled by redirecting to login, so they are not reported
 * to Sentry.
 *
 * `INVALID_GRANT` is deliberately absent: it can indicate a token-rotation
 * failure or backend misconfiguration and is worth investigating.
 * `MISSING_REFRESH_TOKEN` is also absent — it is rate-tracked separately so a
 * spike surfaces a provider scope regression.
 */
const SILENT_AUTH_ERROR_CODES: readonly EAuthErrorCode[] = [
  EAuthErrorCode.LOGIN_REQUIRED,
  EAuthErrorCode.CONSENT_REQUIRED,
  EAuthErrorCode.ACCESS_DENIED,
];

/**
 * Whether a code means the session cannot be salvaged by retrying.
 *
 * @param {EAuthErrorCode} code - Code from an adapter's error mapping
 * @returns {boolean} true when the user has to re-authenticate
 */
export const isNonRecoverableAuthErrorCode = (code: EAuthErrorCode): boolean =>
  NON_RECOVERABLE_AUTH_ERROR_CODES.includes(code);

/**
 * Whether a code is an expected session-lifecycle transition that should not be
 * reported to Sentry.
 *
 * @param {EAuthErrorCode} code - Code from an adapter's error mapping
 * @returns {boolean} true when the failure is handled gracefully by a redirect
 */
export const isSilentAuthErrorCode = (code: EAuthErrorCode): boolean =>
  SILENT_AUTH_ERROR_CODES.includes(code);
